import { useState, useEffect } from 'react';
import { getPendingMatches, getScheduledMatches } from '../services/matches-api.js';

/**
 * Hook for managing match data and state
 * @param {string} [teamId] - Optional team ID to filter matches
 * @param {string} [competitionId] - Optional competition ID to filter pending matches
 * @returns {Object} Match data and loading states
 */
export function useMatches(teamId = null, competitionId = null) {
  const [pendingMatches, setPendingMatches] = useState([]);
  const [scheduledMatches, setScheduledMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPendingMatches = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPendingMatches(competitionId);
      setPendingMatches(data.matches || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch pending matches');
      console.error('Error fetching pending matches:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledMatches = async () => {
    if (!teamId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getScheduledMatches(teamId);
      setScheduledMatches(data.matches || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch scheduled matches');
      console.error('Error fetching scheduled matches:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshMatches = () => {
    fetchPendingMatches();
    if (teamId) {
      fetchScheduledMatches();
    }
  };

  useEffect(() => {
    fetchPendingMatches();
  }, [competitionId]);

  useEffect(() => {
    if (teamId) {
      fetchScheduledMatches();
    }
  }, [teamId]);

  return {
    pendingMatches,
    scheduledMatches,
    loading,
    error,
    refreshMatches,
  };
}

export default useMatches;
